<template>
  <form
    class="search-bar form-group"
    @submit.prevent="handleFormSubmit"
  >
    <label
      class="visually-hidden"
      :for="`search-${namespace}`"
      v-text="$string('general.site_header.search.title')"
    />

    <input
      :id="`search-${namespace}`"
      ref="input"
      v-model="input"
      class="search-bar__input text-body-m-regular"
      :class="{ [$class('active')]: searchQuery !== '' }"
      :placeholder="placeholder || $string('general.site_header.search.placeholder')"
      type="search"
      @click="handleClick"
      @focus="handleFocus"
    >

    <button
      v-if="searchQuery !== ''"
      class="search-bar__clear text-body-m-underline"
      type="button"
      @click.prevent="handleClear"
      v-text="$string('search.clear')"
    />

    <button
      class="search-bar__submit"
      type="submit"
      @blur="handleButtonBlur"
    >
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.search.submit')"
      />

      <icon-search />
    </button>
  </form>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Used in the header.
 * - Acts as a small placeholder to only load predictive search when required.
 *
 * @param {String} namespace - Unique identifier.
 * @param {String} [placeholder] - Replacement placeholder string.
 * @param {Object} redirect - Redirect URL.
 * @param {String} searchQuery - Current search query, used to update input
 * value when clicking suggestion.
 *
 */
import { mapState } from 'vuex'

import IconSearch from '~icons/general/search.svg'

import { trackBloomreachEvent } from '~/helpers/bloomreach'

export default {
  name: 'SearchBar',

  components: {
    IconSearch,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    redirect: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-click': (payload) => typeof payload === 'boolean',
    'search-focus': (payload) => typeof payload === 'boolean',
    'search-input': (payload) => typeof payload === 'string',
    'search-submit': () => true,
    clear: () => true,
  },

  data() {
    return {
      input: '',
      query: '',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      tabbable: (state) => state.accessibility.tabbable,
    }),
  },

  watch: {

    /**
     * Watch input to debounce update query.
     * - Debounces input to set query only when finished typing.
     * - Don't watch query to emit event because search query can update query
     *   when user clicks on a suggestion, don't want to trigger two updates.
     */
    input(query) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        this.query = query
        this.$emit('search-input', this.query)
      }, 300)
    },

    /**
     * Watch search query to update query.
     */
    searchQuery() {
      this.input = this.searchQuery
    },
  },

  methods: {

    /**
     * Handle click to emit event.
     */
    handleClick() {
      this.$emit('search-click', true)
    },

    /**
     * Handle focus to emit event.
     */
    handleFocus() {
      this.$emit('search-focus', true)
    },

    /**
     * Handle button blur.
     * - Assumes that if tabbable is active, there is no query, and button blurs
     *   then the user wants to tab outside the search bar so predictive search
     *   should close.
     */
    handleButtonBlur() {
      if (!this.tabbable || this.query) {
        return
      }

      this.$emit('search-focus', false)
    },

    /**
     * Handle form submit.
     * - If redirect is true, redirect to search page with query.
     * - If redirect is false, emit form submit.
     */
    handleFormSubmit() {
      if (!this.redirect) {
        this.$emit('search-submit')
        return
      }

      /**
       * Bloomreach event handling.
       */
      const email = this.$variable('customer.loggedIn')
        ? this.$variable('customer.email')
        : sessionStorage.getItem('userEmail')

      if (email) {
        const eventParameters = {
          searchQuery: this.input,
          email,
        }

        trackBloomreachEvent('vehicle_search_event', eventParameters)
      }

      const encodedInput = encodeURIComponent(this.input)
      location.href = `${this.$variable('routes.search')}?q=${encodedInput}&type=product`
    },

    /**
     * Handle clear to emit event.
     */
    handleClear() {
      this.$emit('clear')
      this.$refs.input.focus()
    },
  },
}
</script>

<style lang="scss">
@import './search-bar';
</style>
